/**
 * What a running job is doing, and how far along it is.
 *
 * A push spends its first minute downloading a video onto a machine on a mobile link while printing
 * nothing, so a badge reading "running" the whole time is indistinguishable from one that has hung — and the person watching has
 * no basis for deciding whether to keep waiting. The phase names the step; the bar gives it motion,
 * which is what actually answers "is this alive".
 *
 * When there is no phase (queued, or a step nobody measures) it falls back to the plain status rather
 * than inventing a bar out of nothing: a progress indicator that is not driven by real progress is the
 * same lie in a friendlier shape.
 */
import { useEffect, useRef, useState } from 'react'

import type { Job } from '../../api/types'
import { Badge } from '../../ui/components'
import { type Sample, formatEta, formatRate, keepWindow, rateFrom } from './progressRate'

function megabytes(bytes: number): string {
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

export interface PhaseView {
  /** What to show: the phase while one is running, otherwise the plain status. */
  label: string
  /** 0–100, or null when nothing measurable is available — then no bar is drawn. */
  percent: number | null
  /** True when `label` is a live phase rather than the job's status. Explicit, because comparing the
   *  label back to the status would silently misread a phase that happened to be worded the same. */
  isPhase: boolean
}

/** The decision, kept separate from the markup so it can be tested without a DOM. */
export function phaseView(job: Job): PhaseView {
  const running = job.status === 'running' || job.status === 'queued'
  if (!running || !job.phase) return { label: job.status, percent: null, isPhase: false }

  const { done = 0, total = 0 } = job.progress ?? {}
  // A bar needs a real denominator. Without one we still show the phase — "uploading" alone already
  // beats "running" — but no bar, because one that is not driven by progress is the same lie prettier.
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null
  return { label: job.phase, percent, isPhase: true }
}

export function JobPhase({ job }: { job: Job }) {
  const kind = job.status === 'done' ? 'ok' : job.status === 'error' ? 'bad' : 'warn'
  const { label, percent, isPhase } = phaseView(job)
  const { done = 0, total = 0 } = job.progress ?? {}
  const rate = useTransferRate(job.job_id, job.phase ?? null, done, total)
  if (!isPhase) return <Badge kind={kind}>{label}</Badge>

  return (
    <span className="job-phase">
      <Badge kind="warn">{label}</Badge>
      {percent !== null && (
        <>
          <span
            className="job-phase-bar"
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={label}
          >
            <span className="job-phase-fill" style={{ width: `${percent}%` }} />
          </span>
          <span className="dim tiny job-phase-count">
            {percent}% · {megabytes(done)} / {megabytes(total)}
            {rate.bytesPerSec !== null && <> · {formatRate(rate.bytesPerSec)}</>}
            {rate.etaSeconds !== null && rate.etaSeconds > 0 && <> · {formatEta(rate.etaSeconds)} left</>}
          </span>
        </>
      )}
    </span>
  )
}


/**
 * Watch the byte counter and answer how fast it is moving.
 *
 * The samples belong to one (job, phase) pair and are dropped when either changes — the same rule the
 * polling hook follows. Carrying them across would divide an upload's bytes by a download's seconds and
 * produce a confident number about nothing.
 */
function useTransferRate(jobId: string, phase: string | null, done: number, total: number) {
  const [samples, setSamples] = useState<Sample[]>([])
  const subject = useRef<string | null>(null)

  useEffect(() => {
    const key = `${jobId}\u0000${phase}`
    const now = Date.now()
    if (subject.current !== key) {
      subject.current = key
      setSamples([{ done, at: now }])
      return
    }
    setSamples((prev) => {
      // Only a *changed* counter is a sample: polls landing between two of the hub's updates would
      // otherwise fill the window with a flat line and report a stall that is ours, not the link's.
      if (prev[prev.length - 1]?.done === done) return prev
      return keepWindow([...prev, { done, at: now }], now)
    })
  }, [jobId, phase, done])

  return rateFrom(samples, total)
}
